import React, { useEffect, Fragment, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Swal from "sweetalert2";
import Breadcrumb from "../../../components/common/breadcrumb";

// ✅ For Excel & PDF
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // Correct import for pdf
import { MlcReportApi } from "../../../api/ipd/ipdReportsAPI";

const MlcReport = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [type, setType] = useState("");
    const [opdType, setOpdType] = useState("");
    const [ageFilter, setAgeFilter] = useState("");
    const [timeWise, setTimeWise] = useState("");
    const [amount, setAmount] = useState("");
    const [search, setSearch] = useState("");

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const fetchData = async () => {
        dispatch({ type: "LOADER_START" });
        try {
            const data = {
                fromDate: fromDate,
                toDate: toDate,
                type: type,
                opdType: opdType,
                ageFilter: ageFilter,
                timeWise: timeWise,
                amount: amount,
                search: search,
            };
            const response = await MlcReportApi(data);
            if (response.success) {
                showToast("success", response.message);
                const formattedData = response.data.map((item, index) => ({
                    index: index + 1,
                    id: item._id,
                    type: item.docType || "",
                    opdNo: item.opdNo || "",
                    uhidNo: item.uhidNo || "",
                    casually: item.casually || "",
                    status: item.oldPatient === "Yes" ? "Old" : "New",
                    rawDate: item.date || "",
                    date: item.date
                        ? new Date(item.date).toLocaleDateString("en-IN")
                        : "",
                    patientName:
                        (item.patientNamePrefix ? item.patientNamePrefix + " " : "") +
                        (item.patientName || ""),
                    sex: item.sex || "",
                    ageValue: item.age || 0,
                    age: item.age ? `${item.age} ${item.ageSuffix || ""}` : "",
                    mobileNo: item.mobileNo || "",
                    address: item.address || "",
                    consultantDoctor: item.consultantDoctor || "",
                    department: item.departmentId?.name || "",
                    foreiger: item.foreiger || "No",
                    amount: item.amount || 0,
                    discountPercentage: item.discountPercentage || 0,
                    discountAmount: item.discountAmount || 0,
                    netAmount: item.netAmount || 0,
                    paymentMode: item.paymentMode || "",
                    createdBy: item.createdBy?.name || "",
                    createdAt: item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-IN")
                        : "",
                    updatedBy: item.updatedBy?.name || "",
                    updatedAt: item.updatedAt
                        ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                        : "",
                }));
                setData(formattedData);
                setFilteredData(formattedData);
            } else showToast("error", response.message);
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const handleSearch = () => {
        fetchData();
    };

    // Totals
    const totals = useMemo(() => {
        const totalPatients = filteredData.length;
        const newPatients = filteredData.filter((p) => p.status === "new").length;
        const oldPatients = filteredData.filter((p) => p.status === "Old").length;
        const casuallyCount = filteredData.filter((p) => p.casually === "Yes").length;
        const totalAmount = filteredData.reduce(
            (sum, p) => sum + Number(p.amount || 0),
            0
        );
        const cashAmount = filteredData
            .filter((p) => p.paymentMode.toLowerCase() === "cash")
            .reduce((sum, p) => sum + Number(p.netAmount || 0), 0);
        const totalDiscount = filteredData.reduce(
            (sum, p) => sum + Number(p.discountAmount || 0),
            0
        );
        const totalNetAmount = filteredData.reduce(
            (sum, p) => sum + Number(p.netAmount || 0),
            0
        );

        return {
            totalPatients,
            newPatients,
            oldPatients,
            casuallyCount,
            totalAmount,
            cashAmount,
            totalDiscount,
            totalNetAmount,
        };
    }, [filteredData]);

    // Headers (used for CSV, Excel, PDF)
    const headers = [
        { label: "S.No", key: "index" },
        { label: "OPD No", key: "opdNo" },
        { label: "UHID No", key: "uhidNo" },
        { label: "Casually", key: "casually" },
        { label: "Status", key: "status" },
        { label: "Patient Name", key: "patientName" },
        { label: "Sex", key: "sex" },
        { label: "Address", key: "address" },
        { label: "Consultant Name", key: "consultantDoctor" },
        { label: "Department", key: "department" },
        { label: "G. Amount", key: "amount" },
        { label: "Discount %", key: "discountPercentage" },
        { label: "Discount Amt", key: "discountAmount" },
        { label: "Amount", key: "netAmount" },
        { label: "Pay. Mode", key: "paymentMode" },
        { label: "User", key: "createdBy" },
    ];

    // Columns (for table view)
    const columns = headers.map((h) => ({
        name: h.label,
        selector: (row) => row[h.key],
        sortable: true,
        width: "10%",
    }));

    // Export Excel
    const exportToExcel = () => {
        const sheetData = filteredData.map((row) => {
            let obj = {};
            headers.forEach((h) => (obj[h.label] = row[h.key]));
            return obj;
        });

        // Add totals row
        sheetData.push({
            "S.No": "Totals",
            "OPD No": "",
            "UHID No": "",
            "Casually": totals.casuallyCount,
            "Status": "",
            "Patient Name": "",
            "Sex": "",
            "Address": "",
            "Consultant Name": "",
            "Department": "",
            "G. Amount": totals.totalAmount,
            "Discount %": "",
            "Discount Amt": totals.totalDiscount,
            "Amount": totals.totalNetAmount,
            "Pay. Mode": totals.cashAmount,
            "User": "",
        });

        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "OPD Report");
        XLSX.writeFile(workbook, "OPD_Report.xlsx");
    };

    // Export PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("OPD Registration Report", 14, 10);

        const tableColumn = headers.map((h) => h.label);
        const tableRows = filteredData.map((row) => headers.map((h) => row[h.key]));

        // Add totals row at the end
        const totalsRow = [
            "Totals", "", "", totals.casuallyCount, "", "", "", "", "", "",
            totals.totalAmount, "", totals.totalDiscount, totals.totalNetAmount,
            totals.cashAmount, ""
        ];
        tableRows.push(totalsRow);

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("OPD_Report.pdf");
    };

    const resetFilters = () => {
        setFromDate("");
        setToDate("");
        setType("");
        setOpdType("");
        setAgeFilter("");
        setTimeWise("");
        setAmount("");
        setSearch("");
        setFilteredData([])
    };

    return (
        <Fragment>
            <Breadcrumb parent="OPD" title="OPD Registration Report" />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <h5>OPD Registration Report</h5>

                                <div className="row mt-3">
                                    {/* Filters */}
                                    <div className="col-sm-2">
                                        <label>From Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>To Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>Type</label>
                                        <select
                                            className="form-control"
                                            value={type}
                                            onChange={(e) => setType(e.target.value)}
                                        >
                                            <option value="">Select</option>
                                            <option value="IPD">IPD</option>
                                            <option value="OPD">OPD</option>
                                        </select>
                                    </div>

                                    <div className="col-sm-2">
                                        <label>OPD Type</label>
                                        <select
                                            className="form-control"
                                            value={opdType}
                                            onChange={(e) => setOpdType(e.target.value)}
                                        >
                                            <option value="">Select</option>
                                            <option value="Yes">Casually</option>
                                            <option value="No">Regular</option>
                                        </select>
                                    </div>

                                    <div className="col-sm-2">
                                        <label>Age Filter</label>
                                        <select
                                            className="form-control"
                                            value={ageFilter}
                                            onChange={(e) => setAgeFilter(e.target.value)}
                                        >
                                            <option value="">Select</option>
                                            <option value="child">Child (&lt; 18)</option>
                                            <option value="adult">Adult (18-59)</option>
                                            <option value="senior">Senior (60+)</option>
                                        </select>
                                    </div>

                                    <div className="col-sm-2">
                                        <label>Time Wise</label>
                                        <select
                                            className="form-control"
                                            value={timeWise}
                                            onChange={(e) => setTimeWise(e.target.value)}
                                        >
                                            <option value="">Select</option>
                                            <option value="am">AM</option>
                                            <option value="pm">PM</option>
                                        </select>
                                    </div>

                                    <div className="col-sm-2">
                                        <label>Amount ≥</label>
                                        <input
                                            className="form-control"
                                            type="number"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-4">
                                        <label>Search</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search..."
                                        />
                                    </div>
                                    <div className="col-sm">
                                        <button
                                            className="btn btn-primary mt-4 me-2"
                                            onClick={handleSearch}
                                        >
                                            Search
                                        </button>
                                        <button
                                            className="btn btn-warning mt-4"
                                            onClick={resetFilters}
                                        >
                                            Reset
                                        </button>
                                    </div>

                                    {/* Export Buttons */}
                                    <div
                                        className="col-sm text-end"
                                        // style={{ display: "flex", justifyContent: "flex-end" }}
                                    >
                                        <button
                                            className="btn btn-success mt-4 me-2"
                                            onClick={exportToExcel}
                                        >
                                            Excel
                                        </button>
                                        <button
                                            className="btn btn-danger mt-4 me-2"
                                            onClick={exportToPDF}
                                        >
                                            PDF
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Totals */}
                            <div className="card-body">
                                <div className="d-flex flex-wrap gap-3 mb-3">
                                    <span className="badge bg-primary">
                                        Total Patients: {totals.totalPatients}
                                    </span>
                                    <span className="badge bg-success">
                                        New Patients: {totals.newPatients}
                                    </span>
                                    <span className="badge bg-warning">
                                        Old Patients: {totals.oldPatients}
                                    </span>
                                    <span className="badge bg-info">
                                        Casually: {totals.casuallyCount}
                                    </span>
                                    <span className="badge bg-secondary">
                                        Total Amount: ₹{totals.totalAmount}
                                    </span>
                                    <span className="badge bg-dark">
                                        Cash Amount: ₹{totals.cashAmount}
                                    </span>
                                    <span className="badge bg-danger">
                                        Total Discount: ₹{totals.totalDiscount}
                                    </span>
                                    <span className="badge bg-success">
                                        Total Net Amount: ₹{totals.totalNetAmount}
                                    </span>
                                </div>

                                {/* Data Table */}
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    pagination
                                    highlightOnHover
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default MlcReport;
